/* eslint-disable max-len */
import React from 'react'
import Button from '@components/common/Button/Button.component'
import { INote } from '@dataTypes/note.type'
import {
  formatNoteTitleWithVersion,
  hasMultipleVersionsInChain,
} from '@utils/noteVersion.util'
import type { NoteMicMode } from './noteEditor.types'

type NotesForContextHeaderProps = {
  isPending: boolean | string
  contextName: string | null | undefined
}

const NotesForContextHeader = ({
  isPending,
  contextName,
}: NotesForContextHeaderProps): JSX.Element => (
  <span className="add-note-title-context">
    Notes for {Boolean(isPending) ? 'Loading...' : (contextName ?? '')}:{' '}
  </span>
)

const formatHeaderTitle = (
  displayedNote: INote | null,
  versionSiblingPool: INote[],
  noteTitle: string,
): string =>
  displayedNote && hasMultipleVersionsInChain(displayedNote, versionSiblingPool)
    ? formatNoteTitleWithVersion({
        title: noteTitle,
        version: displayedNote?.version,
      })
    : noteTitle

type NoteEditorHeaderProps = {
  backLabel: string | undefined
  onBackClick: () => void
  selectFirstNotePending: boolean
  isViewMode: boolean
  isHeaderContextPending: boolean | string
  headerContextName: string | null
  displayedNote: INote | null
  versionSiblingPool: INote[]
  noteTitle: string
  onNoteTitleChange: (value: string) => void
  micModeLabel: string
  isRecordingActive: boolean
  isUpdatingTranscription: boolean
  micStatusLabel: string
  isSmartMode: boolean
  isRecording: boolean
  isRecordingPaused: boolean
  isLiveTranscribeConnecting: boolean
  onMicClick: (event: React.MouseEvent) => void
  flashModeTestUrl: string | null
  flashModeAudioRef: React.RefObject<HTMLAudioElement | null>
  isFlashMode: boolean
  isFlashModeTesting: boolean
  hasRecordedAudio: boolean
  onResetRecordingRequest: (
    event?: React.MouseEvent | React.KeyboardEvent,
  ) => void
  onRecordingStop: (event: React.MouseEvent) => void
  onRecordingPause: (event: React.MouseEvent) => void
  onRecordingCircleClick: (event: React.MouseEvent) => void
  activeMic: NoteMicMode
  onActiveMicChange: (mic: NoteMicMode) => void
  canEditDisplayedNoteVersion: boolean
  claimContextNotesReadOnly: boolean
  hasContextId: boolean
  hasContextNotesOwnership: boolean
  onSetActiveMode: (mode: 'view' | 'edit') => void
  onEditNote?: (note?: INote | null) => void
  onCancel: () => void
  onSave: () => void
}

const NoteEditorHeader = ({
  backLabel,
  onBackClick,
  selectFirstNotePending,
  isViewMode,
  isHeaderContextPending,
  headerContextName,
  displayedNote,
  versionSiblingPool,
  noteTitle,
  onNoteTitleChange,
  micModeLabel,
  isRecordingActive,
  isUpdatingTranscription,
  micStatusLabel,
  isSmartMode,
  isRecording,
  isRecordingPaused,
  isLiveTranscribeConnecting,
  onMicClick,
  flashModeTestUrl,
  flashModeAudioRef,
  isFlashMode,
  isFlashModeTesting,
  hasRecordedAudio,
  onResetRecordingRequest,
  onRecordingStop,
  onRecordingPause,
  onRecordingCircleClick,
  activeMic,
  onActiveMicChange,
  canEditDisplayedNoteVersion,
  claimContextNotesReadOnly,
  hasContextId,
  hasContextNotesOwnership,
  onSetActiveMode,
  onEditNote,
  onCancel,
  onSave,
}: NoteEditorHeaderProps): JSX.Element => {
  const headerTitle = formatHeaderTitle(
    displayedNote,
    versionSiblingPool,
    noteTitle,
  )
  const editDisabled = !canEditDisplayedNoteVersion || claimContextNotesReadOnly
  return (
    <div className="notes-main-content-top">
      <div className="add-note-header-left">
        <button
          type="button"
          className="add-note-back-button"
          title={backLabel}
          aria-label={backLabel}
          onClick={onBackClick}
          style={{ cursor: 'pointer' }}
        >
          <i className="mgc mgc_left_line" aria-hidden />
        </button>
        {selectFirstNotePending ? null : !isViewMode ? (
          <>
            <NotesForContextHeader
              isPending={isHeaderContextPending}
              contextName={headerContextName}
            />
            {displayedNote?.note_id?.trim() ? (
              <span className="add-note-title">{headerTitle}</span>
            ) : (
              <input
                type="text"
                value={noteTitle}
                onChange={(e) => onNoteTitleChange(e.target.value)}
                className="add-note-title-input"
                autoFocus
              />
            )}
          </>
        ) : (
          <>
            <NotesForContextHeader
              isPending={isHeaderContextPending}
              contextName={headerContextName}
            />
            <span className="add-note-title">{headerTitle}</span>
          </>
        )}
      </div>
      <div className="add-note-header-right">
        {selectFirstNotePending ? null : (
          <>
            {!isViewMode && (
              <>
                <div className="notes-mic-info">
                  <span className="notes-mic-info-title">{micModeLabel}</span>
                  <span
                    className={`notes-mic-info-action ${
                      isRecordingActive ? 'text-red-500' : ''
                    } ${
                      isUpdatingTranscription
                        ? 'notes-mic-info-action--updating'
                        : ''
                    }`}
                  >
                    {micStatusLabel}
                  </span>
                </div>
                <div
                  className={`notes-mic-container ${
                    isSmartMode && isRecording && !isRecordingPaused
                      ? 'notes-mic-container-recording'
                      : ''
                  } ${
                    isSmartMode && isRecordingPaused
                      ? 'notes-mic-container-paused'
                      : ''
                  } ${
                    isLiveTranscribeConnecting || isUpdatingTranscription
                      ? 'notes-mic-container-connecting'
                      : ''
                  }`}
                  onClick={onMicClick}
                >
                  {flashModeTestUrl && (
                    <audio
                      ref={flashModeAudioRef as React.Ref<HTMLAudioElement>}
                      style={{ display: 'none' }}
                    />
                  )}
                  {(isSmartMode && isRecording) ||
                  (isFlashMode && isFlashModeTesting) ? (
                    <div className="notes-mic-wave">
                      <span className="notes-mic-wave-line notes-mic-wave-line--tall" />
                      <span className="notes-mic-wave-line notes-mic-wave-line--medium" />
                      <span className="notes-mic-wave-line notes-mic-wave-line--short" />
                      <span className="notes-mic-wave-line notes-mic-wave-line--medium" />
                      <span className="notes-mic-wave-line notes-mic-wave-line--tall" />
                    </div>
                  ) : (
                    <div
                      className={`notes-mic-spinner ${
                        hasRecordedAudio ? 'notes-mic-spinner--reset' : ''
                      }`}
                      onClick={
                        hasRecordedAudio ? onResetRecordingRequest : undefined
                      }
                      onKeyDown={(event) => {
                        if (
                          hasRecordedAudio &&
                          (event.key === 'Enter' || event.key === ' ')
                        ) {
                          onResetRecordingRequest(event)
                        }
                      }}
                      role={hasRecordedAudio ? 'button' : undefined}
                      tabIndex={hasRecordedAudio ? 0 : -1}
                      aria-label={
                        hasRecordedAudio
                          ? 'Reset recording'
                          : 'No recording available to reset'
                      }
                    >
                      <i className="mgc mgc_refresh_anticlockwise_1_line" />
                    </div>
                  )}
                  <div
                    className={`notes-mic-status ${
                      (isSmartMode && isRecording) ||
                      (isFlashMode && isFlashModeTesting)
                        ? 'notes-mic-status--recording'
                        : 'notes-mic-status--idle'
                    } ${
                      isSmartMode && isRecordingPaused
                        ? 'notes-mic-status--paused'
                        : ''
                    }`}
                  >
                    {((isSmartMode && isRecording) ||
                      (isFlashMode && isFlashModeTesting)) && (
                      <button
                        type="button"
                        className="notes-mic-status-square"
                        onClick={onRecordingStop}
                        aria-label="Stop recording"
                        disabled={
                          (!isSmartMode && !isFlashMode) ||
                          (!isRecording && !isFlashModeTesting)
                        }
                        aria-disabled={
                          (!isSmartMode && !isFlashMode) ||
                          (!isRecording && !isFlashModeTesting)
                        }
                      />
                    )}
                    {isSmartMode &&
                      isRecording &&
                      !isRecordingPaused &&
                      !isLiveTranscribeConnecting && (
                        <button
                          type="button"
                          className="notes-mic-status-pause"
                          onClick={onRecordingPause}
                          aria-label="Pause recording"
                        >
                          <i className="mgc mgc_pause_fill" />
                        </button>
                      )}
                    {((isSmartMode &&
                      (!isRecording ||
                        (isRecordingPaused && !isLiveTranscribeConnecting))) ||
                      (isFlashMode && !isFlashModeTesting)) && (
                      <button
                        type="button"
                        className="notes-mic-status-circle"
                        onClick={onRecordingCircleClick}
                        disabled={isSmartMode && isUpdatingTranscription}
                        aria-disabled={isSmartMode && isUpdatingTranscription}
                        aria-label={
                          isUpdatingTranscription
                            ? 'Updating transcription'
                            : isSmartMode
                              ? isRecordingPaused
                                ? 'Resume recording'
                                : 'Record'
                              : 'Test microphone'
                        }
                      />
                    )}
                  </div>
                  <div className="notes-mic-buttons">
                    <span
                      className={`notes-mic-button ${
                        activeMic === 'standard'
                          ? 'notes-mic-button-active'
                          : ''
                      } ${
                        isRecording && activeMic === 'standard'
                          ? 'notes-mic-button-recording'
                          : ''
                      }`}
                      onClick={(event) => {
                        event.stopPropagation()
                        onActiveMicChange('standard')
                      }}
                    >
                      <i className="mgc mgc_mic_fill" />
                    </span>
                    <span
                      className={`notes-mic-button notes-mic-button-ai ${
                        activeMic === 'ai' ? 'notes-mic-button-active' : ''
                      } ${
                        isRecording && activeMic === 'ai'
                          ? 'notes-mic-button-recording'
                          : ''
                      }`}
                      role="button"
                      title="Smart mode"
                      onClick={(event) => {
                        event.stopPropagation()
                        onActiveMicChange('ai')
                      }}
                    >
                      <i className="mgc mgc_mic_ai_fill" />
                    </span>
                  </div>
                </div>
              </>
            )}
            {selectFirstNotePending ? null : isViewMode ? (
              <Button
                size="md"
                className="button-primary"
                disabled={editDisabled}
                title={
                  claimContextNotesReadOnly
                    ? 'Notes can only be edited when the claim is rejected.'
                    : !canEditDisplayedNoteVersion
                      ? 'Only the latest version of this note can be edited.'
                      : undefined
                }
                onClick={() => {
                  if (editDisabled) return
                  if (hasContextId || hasContextNotesOwnership) {
                    onSetActiveMode('edit')
                  } else {
                    onEditNote?.(displayedNote ?? null)
                  }
                }}
              >
                Edit note
              </Button>
            ) : (
              <>
                <Button
                  size="md"
                  className="button-secondary button-icon-round"
                  onClick={onCancel}
                >
                  Cancel
                </Button>
                <Button
                  size="md"
                  className="button-primary button-icon-round"
                  onClick={onSave}
                >
                  Save note
                </Button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default NoteEditorHeader
